import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Paginated } from '@/shared/api/client';
import type { Report, ReportStatus } from '@/shared/api/types';

export interface ReportFilters {
  status?: ReportStatus | '';
  projectId?: string;
}

export function useReports(filters: ReportFilters) {
  const params = new URLSearchParams({ limit: '100' });
  if (filters.status) params.set('status', filters.status);
  if (filters.projectId) params.set('projectId', filters.projectId);
  return useQuery({
    queryKey: ['reports', filters],
    queryFn: () => api<Paginated<Report>>(`/api/reports?${params.toString()}`),
  });
}

export function useReport(id: string | null, opts?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: ['report', id],
    queryFn: () => api<Report>(`/api/reports/${id}`),
    enabled: !!id,
    refetchInterval: opts?.refetchInterval ?? false,
  });
}

/** Entwurf anstossen. Der Worker braucht einen Moment - die Liste pollt danach. */
export function useGenerateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      api<{ jobId: string; message: string }>(`/api/reports/generate/${projectId}`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      // Nicht sofort invalidieren - der Bericht existiert erst nach dem Worker.
      setTimeout(() => qc.invalidateQueries({ queryKey: ['reports'] }), 4000);
      setTimeout(() => qc.invalidateQueries({ queryKey: ['reports'] }), 10000);
    },
  });
}

export interface UpdateReportInput {
  summary?: string;
  workDescription?: string;
}

export function useUpdateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateReportInput }) =>
      api<Report>(`/api/reports/${id}`, { method: 'PATCH', body: input }),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ['reports'] });
      qc.invalidateQueries({ queryKey: ['report', id] });
    },
  });
}

export function useApproveReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      api<Report>(`/api/reports/${id}/approve`, {
        method: 'PATCH',
        body: note ? { note } : {},
      }),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ['reports'] });
      qc.invalidateQueries({ queryKey: ['report', id] });
    },
  });
}

export function useGeneratePdf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ mediaId: string; url: string; size: number; regenerated: boolean }>(
        `/api/reports/${id}/pdf`,
        { method: 'POST', body: {} },
      ),
    onSuccess: (_d, id) => qc.invalidateQueries({ queryKey: ['report', id] }),
  });
}

export function usePdfUrl() {
  return useMutation({
    mutationFn: (id: string) => api<{ url: string; size: number }>(`/api/reports/${id}/pdf`),
  });
}

// Kundenfreigabe senden
export interface CreateApprovalInput {
  reportId: string;
  extraServiceIds: string[];
  contactId?: string;
  validDays?: number;
}

export function useSendApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateApprovalInput) =>
      api<{ url: string; approvalId: string; expiresAt: string; totalNet: string }>(
        '/api/customer-approvals',
        { method: 'POST', body: input },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports'] });
      qc.invalidateQueries({ queryKey: ['report'] });
      qc.invalidateQueries({ queryKey: ['extra-services'] });
    },
  });
}

/** Kontakte + bestaetigte Zusatzleistungen fuer den Sende-Dialog. */
export function useApprovalPrereqs(projectId: string | null, customerId: string | null) {
  const contacts = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () =>
      api<{ contacts: { id: string; name: string; approvalAuthorized: boolean }[] }>(
        `/api/customers/${customerId}`,
      ),
    enabled: !!customerId,
  });
  const extras = useQuery({
    queryKey: ['project-extras', projectId],
    queryFn: () =>
      api<Paginated<{ id: string; title: string; netAmount: string | null; confirmedInternal: boolean; _count?: { approvalItems: number } }>>(
        `/api/extra-services?projectId=${projectId}&confirmedInternal=true&limit=100`,
      ),
    enabled: !!projectId,
  });
  return { contacts, extras };
}
